import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import {
  Target,
  Plus,
  X,
  Trash2,
  Loader2,
  AlertCircle,
  Pencil,
  Check,
  Search,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const PRIORITY_TONE: Record<number, string> = {
  1: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/40",
  2: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40",
  3: "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/40",
};

/**
 * Manager/owner dialog for setting weekly priorities per team
 * member. Each member gets their own section with an inline add
 * row + a list of current priorities.
 *
 * ALIGNMENT, NOT SURVEILLANCE: this dialog manages assignments
 * (create / edit / delete / carry-forward) and shows assignment
 * metadata only. The server returns teammates' rows WITHOUT live
 * status, there are no status toggles on other people's work, and
 * no "matching signals" evidence chips (those read private
 * captures). A member's progress reaches the manager only through
 * an approved receipt.
 *
 * The "weekStartKey" prop comes from Canvas so the dialog always
 * targets the same week the user is currently viewing in the
 * WeekRibbon. Flipping weeks in the ribbon → reopening the dialog
 * → new week's priorities.
 */
export function SetTeamPrioritiesDialog({
  open,
  onOpenChange,
  teamId,
  weekStartKey,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  teamId: number | null;
  weekStartKey: string;
}) {
  const { locale } = useI18n();
  const utils = trpc.useUtils();

  // Internal week navigation. Opens to the week passed in
  // (`weekStartKey` prop) but the manager can flip prev/next from
  // inside the dialog — essential for the carry-forward workflow:
  // open dialog → navigate to NEXT week → carry banner shows the
  // current week's unfinished items as candidates.
  //
  // `weekOffset` is relative to the initial prop (0 = the week the
  // dialog opened to). Reset every time `weekStartKey` changes so
  // reopening always starts at the latest prop value.
  const [weekOffset, setWeekOffset] = useState(0);
  // Reset offset when the dialog opens for a different starting
  // week (e.g. user clicked the Target icon at a different time of
  // day, picking up a new "current week").
  useEffect(() => {
    setWeekOffset(0);
  }, [weekStartKey]);

  const activeWeekStartKey = useMemo(() => {
    if (weekOffset === 0) return weekStartKey;
    const d = new Date(weekStartKey + "T00:00:00.000Z");
    d.setUTCDate(d.getUTCDate() + weekOffset * 7);
    return d.toISOString().slice(0, 10);
  }, [weekStartKey, weekOffset]);

  const membersQ = trpc.team.members.useQuery(
    { teamId: teamId ?? -1 },
    { enabled: !!teamId && open },
  );
  const prioritiesQ = trpc.priorities.forWeek.useQuery(
    { teamId: teamId ?? -1, weekStartKey: activeWeekStartKey },
    { enabled: !!teamId && open },
  );
  const createMut = trpc.priorities.create.useMutation();
  const setStatusMut = trpc.priorities.setStatus.useMutation();
  const deleteMut = trpc.priorities.delete.useMutation();
  const updateMut = trpc.priorities.update.useMutation();
  const carrySelectedMut = trpc.priorities.carrySelected.useMutation();

  // Prior-week key (UTC, -7d from the currently viewed week).
  // Recomputes when the in-dialog week navigator moves so the carry
  // banner offers candidates from the right prior week.
  const priorWeekKey = useMemo(() => {
    const cur = new Date(activeWeekStartKey + "T00:00:00.000Z");
    const prior = new Date(cur);
    prior.setUTCDate(cur.getUTCDate() - 7);
    return prior.toISOString().slice(0, 10);
  }, [activeWeekStartKey]);

  // Carry candidates — unfinished priorities from last week that
  // aren't already duplicated in this week. Drives the banner.
  const candidatesQ = trpc.priorities.carryCandidates.useQuery(
    {
      teamId: teamId ?? -1,
      fromWeekStartKey: priorWeekKey,
      toWeekStartKey: activeWeekStartKey,
    },
    { enabled: !!teamId && open, staleTime: 30_000 },
  );
  const candidates = candidatesQ.data ?? [];

  // Dismiss state — persisted to localStorage so the banner doesn't
  // re-appear after the manager skips. Keyed by (team, week) so a
  // dismissal on this week doesn't suppress next week's banner.
  const dismissKey = teamId
    ? `priorities:carryDismissed:${teamId}:${activeWeekStartKey}`
    : null;
  const [dismissed, setDismissed] = useState<boolean>(false);
  // Re-evaluate the dismissed flag whenever the user navigates to a
  // different week — a "skipped" decision on week N shouldn't
  // suppress the banner on week N+1.
  useEffect(() => {
    if (typeof window === "undefined" || !dismissKey) {
      setDismissed(false);
      return;
    }
    setDismissed(window.localStorage.getItem(dismissKey) === "1");
  }, [dismissKey]);
  function dismiss() {
    if (dismissKey) window.localStorage.setItem(dismissKey, "1");
    setDismissed(true);
  }

  // Review modal state — when open, shows the checklist. Selected
  // IDs default to "all". Reassignments are tracked separately so
  // a manager can leave the assignee alone (default = source) and
  // only set the map entry when they actually change it.
  const [reviewOpen, setReviewOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [reassignBy, setReassignBy] = useState<Record<number, number>>({});
  const [repriorityBy, setRepriorityBy] = useState<Record<number, number>>({});
  function openReview() {
    setSelectedIds(new Set(candidates.map(c => c.id)));
    setReassignBy({});
    setRepriorityBy({});
    setReviewOpen(true);
  }
  function toggleSelected(id: number) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Track which candidate rows are mid-drop so the button can show a
  // spinner and we can disable double-clicks.
  const [droppingIds, setDroppingIds] = useState<Set<number>>(new Set());

  /**
   * Remove an individual carry candidate. Unchecking a row only
   * skips it for THIS carry — it stays `pending`/`in_progress` and
   * re-surfaces as a candidate next week. "Remove" instead DROPS the
   * source priority (status → `dropped`) so it's intentionally
   * retired: it won't carry now and won't nag again, while still
   * leaving an audit trail (the dept-report priority audit treats
   * `dropped` specially rather than pretending it never existed).
   */
  async function dropCandidate(id: number, title: string) {
    if (
      !window.confirm(
        locale === "ar"
          ? `إزالة "${title}" نهائياً؟ لن تُرحَّل ولن تظهر مرة أخرى.`
          : `Remove "${title}"? It won't carry forward and won't be suggested again.`,
      )
    ) {
      return;
    }
    setDroppingIds(prev => new Set(prev).add(id));
    try {
      await setStatusMut.mutateAsync({ priorityId: id, status: "dropped" });
      utils.priorities.forWeek.invalidate();
      utils.priorities.carryCandidates.invalidate();
      // Purge any local review state for this row so it can't be
      // re-selected / committed after it's gone.
      setSelectedIds(prev => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setReassignBy(prev => {
        if (prev[id] == null) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setRepriorityBy(prev => {
        if (prev[id] == null) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      toast.success(
        locale === "ar" ? "تمت إزالة الأولوية" : "Priority removed",
      );
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn't remove");
    } finally {
      setDroppingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  /**
   * Commit a carry. `items` controls both selection AND reassignment:
   *   - "Carry all" (from banner) → all candidates, no reassignment
   *   - "Carry N" (from review)   → selected subset + any picked
   *                                 reassignee overrides
   */
  async function commitCarry(
    items: Array<{
      priorityId: number;
      assigneeUserId?: number;
      priorityLevel?: number;
    }>,
  ) {
    if (items.length === 0 || !teamId) return;
    try {
      const r = await carrySelectedMut.mutateAsync({
        items,
        toWeekStartKey: activeWeekStartKey,
      });
      utils.priorities.forWeek.invalidate();
      utils.priorities.carryCandidates.invalidate();
      setReviewOpen(false);
      dismiss();
      toast.success(
        locale === "ar"
          ? `تمّ ترحيل ${r.carried} أولوية`
          : `Carried ${r.carried} ${r.carried === 1 ? "priority" : "priorities"}${
              r.skipped ? ` (${r.skipped} duplicate)` : ""
            }`,
      );
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn't carry forward");
    }
  }

  // Member-name search — when the team grows past ~5 people the
  // dialog gets long and finding the right person is annoying.
  // Filter is case-insensitive substring on the displayed name.
  const [memberQuery, setMemberQuery] = useState("");

  // Inline edit state — one priority at a time. Tracks title +
  // priority level so a manager can fix a typo AND rerank in the
  // same edit. Description still requires the full dialog to keep
  // the inline UX tight.
  const [editing, setEditing] = useState<{
    id: number;
    title: string;
    priority: number;
  } | null>(null);

  async function saveEdit() {
    if (!editing) return;
    const title = editing.title.trim();
    if (!title) {
      toast.error(locale === "ar" ? "العنوان فارغ" : "Title is empty");
      return;
    }
    try {
      await updateMut.mutateAsync({
        priorityId: editing.id,
        title,
        priority: editing.priority,
      });
      setEditing(null);
      utils.priorities.forWeek.invalidate();
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn't save");
    }
  }

  // Per-member draft form state — keys are userId, values are the
  // in-progress new priority for that member.
  const [drafts, setDrafts] = useState<
    Record<number, { title: string; description: string; priority: number }>
  >({});

  function getDraft(userId: number) {
    return (
      drafts[userId] ?? { title: "", description: "", priority: 2 }
    );
  }
  function setDraft(
    userId: number,
    next: Partial<{ title: string; description: string; priority: number }>,
  ) {
    setDrafts(prev => ({
      ...prev,
      [userId]: { ...getDraft(userId), ...next },
    }));
  }

  async function addPriority(userId: number) {
    const d = getDraft(userId);
    const title = d.title.trim();
    if (!title) return;
    if (!teamId) return;
    try {
      await createMut.mutateAsync({
        teamId,
        userId,
        weekStartKey: activeWeekStartKey,
        title,
        description: d.description.trim() || undefined,
        priority: d.priority,
      });
      setDrafts(prev => ({
        ...prev,
        [userId]: { title: "", description: "", priority: 2 },
      }));
      utils.priorities.forWeek.invalidate();
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn't add");
    }
  }

  // NOTE: there is intentionally no status toggle here. Progress is
  // employee-owned — the server rejects a manager setting another
  // member's status (except `dropped` via the carry review's Remove,
  // which retires the priority itself).

  async function removePriority(id: number) {
    try {
      await deleteMut.mutateAsync({ priorityId: id });
      utils.priorities.forWeek.invalidate();
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn't delete");
    }
  }

  const allMembers = membersQ.data ?? [];
  const allPriorities = prioritiesQ.data ?? [];

  // Filtered member list — applied only when the search box has
  // text. We keep the original order otherwise (manager-defined).
  const members = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    if (!q) return allMembers;
    return allMembers.filter(m =>
      (m.user.name ?? "").toLowerCase().includes(q),
    );
  }, [allMembers, memberQuery]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="
          sm:max-w-2xl sm:max-h-[88dvh]
          max-sm:top-auto max-sm:bottom-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0
          max-sm:w-full max-sm:max-w-none max-sm:h-[92dvh] max-sm:max-h-[92dvh]
          max-sm:rounded-b-none max-sm:rounded-t-2xl
          flex flex-col p-0 gap-0 overflow-hidden
        "
      >
        <div className="sm:hidden flex justify-center pt-2 pb-1 shrink-0">
          <div className="h-1 w-10 rounded-full bg-muted-foreground/30" />
        </div>

        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 shrink-0 border-b border-border/60">
          <div className="flex-1 min-w-0">
            <DialogTitle className="font-display text-lg flex items-center gap-2">
              <Target className="h-5 w-5 text-coral" />
              {locale === "ar"
                ? "أولويّات الفريق للأسبوع"
                : "Team priorities this week"}
            </DialogTitle>
            {/* In-dialog week navigator — flip prev/next without
                closing the dialog. Essential for the carry-forward
                workflow: open at current week → click ▶ → see the
                carry banner offering this week's unfinished items
                as candidates for next week. */}
            <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
              <button
                type="button"
                onClick={() => setWeekOffset(o => o - 1)}
                className="h-6 w-6 grid place-items-center rounded hover:bg-muted text-muted-foreground"
                aria-label={
                  locale === "ar" ? "أسبوع سابق" : "Previous week"
                }
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <span className="px-1 tabular-nums">
                {locale === "ar" ? "أسبوع" : "Week of"}{" "}
                <strong className="text-foreground">{activeWeekStartKey}</strong>
                {weekOffset !== 0 && (
                  <span className="ms-1 text-[10px] text-coral">
                    {weekOffset > 0
                      ? `(+${weekOffset})`
                      : `(${weekOffset})`}
                  </span>
                )}
              </span>
              <button
                type="button"
                onClick={() => setWeekOffset(o => o + 1)}
                className="h-6 w-6 grid place-items-center rounded hover:bg-muted text-muted-foreground"
                aria-label={
                  locale === "ar" ? "الأسبوع التالي" : "Next week"
                }
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label={locale === "ar" ? "إغلاق" : "Close"}
            className="shrink-0 h-9 w-9 grid place-items-center rounded-full border border-border bg-background hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-5">
          {/* Carry-forward banner — surfaces only when prior week
              has unfinished priorities AND the manager hasn't
              dismissed for this (team, week). Three actions:
                Review     — opens checklist
                Carry all  — one click, ship everything
                Skip       — dismisses for this (team, week) */}
          {candidates.length > 0 && !dismissed && !reviewOpen && (
            <div className="rounded-lg border border-coral/40 bg-coral-soft/20 px-3 py-2.5 flex items-start gap-2 flex-wrap">
              <RotateCcw className="h-4 w-4 text-coral shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0 text-sm">
                <div className="font-medium text-foreground">
                  {locale === "ar"
                    ? `${candidates.length} ${candidates.length === 1 ? "أولوية" : "أولويّات"} من الأسبوع السابق لم تكتمل`
                    : `${candidates.length} ${candidates.length === 1 ? "priority" : "priorities"} from last week weren't finished`}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  {locale === "ar"
                    ? "هل ترغب بترحيلها إلى هذا الأسبوع؟"
                    : "Roll them into this week?"}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <Button
                  onClick={openReview}
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                >
                  {locale === "ar" ? "مراجعة" : "Review"}
                </Button>
                <Button
                  onClick={() =>
                    commitCarry(
                      candidates.map(c => ({ priorityId: c.id })),
                    )
                  }
                  disabled={carrySelectedMut.isPending}
                  size="sm"
                  className="h-7 text-xs bg-coral hover:bg-coral/90 text-white gap-1"
                >
                  {carrySelectedMut.isPending && (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  )}
                  {locale === "ar" ? "ترحيل الكل" : "Carry all"}
                </Button>
                <button
                  type="button"
                  onClick={dismiss}
                  className="text-[11px] text-muted-foreground hover:text-foreground px-1"
                >
                  {locale === "ar" ? "تخطّي" : "Skip"}
                </button>
              </div>
            </div>
          )}

          {/* Review checklist — inline panel (not a nested dialog
              because nesting dialogs has focus-trap gotchas). Mgr
              ticks which items to roll, then commits. */}
          {reviewOpen && candidates.length > 0 && (
            <div className="rounded-lg border border-coral/40 bg-background p-3 space-y-2">
              <div className="flex items-center gap-2">
                <RotateCcw className="h-4 w-4 text-coral shrink-0" />
                <div className="text-sm font-medium flex-1">
                  {locale === "ar"
                    ? "اختر الأولويّات للترحيل"
                    : "Choose priorities to carry forward"}
                </div>
                <button
                  type="button"
                  onClick={() => setReviewOpen(false)}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label={locale === "ar" ? "إغلاق" : "Close"}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                <button
                  type="button"
                  onClick={() =>
                    setSelectedIds(new Set(candidates.map(c => c.id)))
                  }
                  className="hover:text-coral"
                >
                  {locale === "ar" ? "تحديد الكل" : "Select all"}
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedIds(new Set())}
                  className="hover:text-coral"
                >
                  {locale === "ar" ? "إلغاء التحديد" : "Select none"}
                </button>
                <span className="ms-auto">
                  {selectedIds.size}/{candidates.length}
                </span>
              </div>
              <ul className="space-y-1">
                {candidates.map(c => {
                  const checked = selectedIds.has(c.id);
                  const tone = PRIORITY_TONE[c.priority] ?? PRIORITY_TONE[2];
                  const cc = (c as any).carryCount ?? 0;
                  return (
                    <li
                      key={c.id}
                      className="flex items-start gap-2 text-sm rounded-md border border-border/60 px-2 py-1.5 hover:bg-muted/40 cursor-pointer"
                      onClick={() => toggleSelected(c.id)}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSelected(c.id)}
                        onClick={e => e.stopPropagation()}
                        className="mt-1 accent-coral"
                      />
                      {/* P-level selector — defaults to source. The
                          manager can re-rank during the carry (e.g.
                          a stale P1 is now P3 next week). */}
                      <select
                        value={repriorityBy[c.id] ?? c.priority}
                        onClick={e => e.stopPropagation()}
                        onChange={e => {
                          e.stopPropagation();
                          const v = parseInt(e.target.value, 10);
                          setRepriorityBy(prev => {
                            const next = { ...prev };
                            if (v === c.priority) delete next[c.id];
                            else next[c.id] = v;
                            return next;
                          });
                        }}
                        className={`shrink-0 text-[10px] uppercase rounded-full border px-1 py-0.5 mt-0.5 bg-background outline-none focus:border-coral ${
                          (repriorityBy[c.id] ?? c.priority) === c.priority
                            ? tone
                            : "border-amber-500/60 text-amber-700 dark:text-amber-300"
                        }`}
                      >
                        <option value={1}>P1</option>
                        <option value={2}>P2</option>
                        <option value={3}>P3</option>
                      </select>
                      <div className="flex-1 min-w-0">
                        <div className="leading-snug break-words">
                          {c.title}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                          {/* Assignee picker — defaults to the source
                              row's assignee. Changing it sets an
                              override that's sent with the carry
                              request. Click on the <select> doesn't
                              toggle the checkbox (stopPropagation). */}
                          <select
                            value={reassignBy[c.id] ?? c.userId}
                            onClick={e => e.stopPropagation()}
                            onChange={e => {
                              e.stopPropagation();
                              const v = parseInt(e.target.value, 10);
                              setReassignBy(prev => {
                                const next = { ...prev };
                                if (v === c.userId) delete next[c.id];
                                else next[c.id] = v;
                                return next;
                              });
                            }}
                            className="bg-background border border-border rounded px-1 py-0.5 text-[11px] outline-none focus:border-coral max-w-[160px]"
                          >
                            {allMembers.map(m => (
                              <option key={m.user.id} value={m.user.id}>
                                {m.user.name}
                              </option>
                            ))}
                          </select>
                          {reassignBy[c.id] != null &&
                            reassignBy[c.id] !== c.userId && (
                              <span className="text-amber-700 dark:text-amber-300 italic">
                                {locale === "ar"
                                  ? `← من ${(c as any).assigneeName}`
                                  : `← from ${(c as any).assigneeName}`}
                              </span>
                            )}
                          {cc > 0 && (
                            <span
                              className={`${cc >= 2 ? "text-amber-700 dark:text-amber-300 font-medium" : ""}`}
                            >
                              {locale === "ar"
                                ? `مُرحَّل ×${cc}`
                                : `carried ×${cc}`}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Remove (drop) this candidate — distinct from
                          un-checking, which only skips it this round.
                          stopPropagation so the click doesn't toggle
                          the row's checkbox. */}
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          dropCandidate(c.id, c.title);
                        }}
                        disabled={droppingIds.has(c.id)}
                        className="shrink-0 mt-0.5 p-1 rounded text-muted-foreground hover:text-red-600 hover:bg-red-500/10 disabled:opacity-50"
                        title={locale === "ar" ? "إزالة" : "Remove"}
                        aria-label={locale === "ar" ? "إزالة" : "Remove"}
                      >
                        {droppingIds.has(c.id) ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div className="flex items-center gap-2 pt-1">
                <Button
                  onClick={() =>
                    commitCarry(
                      Array.from(selectedIds).map(id => {
                        const item: {
                          priorityId: number;
                          assigneeUserId?: number;
                          priorityLevel?: number;
                        } = { priorityId: id };
                        if (reassignBy[id] != null)
                          item.assigneeUserId = reassignBy[id];
                        if (repriorityBy[id] != null)
                          item.priorityLevel = repriorityBy[id];
                        return item;
                      }),
                    )
                  }
                  disabled={
                    carrySelectedMut.isPending || selectedIds.size === 0
                  }
                  size="sm"
                  className="h-7 text-xs bg-coral hover:bg-coral/90 text-white gap-1"
                >
                  {carrySelectedMut.isPending && (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  )}
                  {locale === "ar"
                    ? `ترحيل ${selectedIds.size}`
                    : `Carry ${selectedIds.size}`}
                </Button>
                <Button
                  onClick={() => setReviewOpen(false)}
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                >
                  {locale === "ar" ? "إلغاء" : "Cancel"}
                </Button>
              </div>
            </div>
          )}

          {/* Member search — only show when the team has enough
              people that scrolling is annoying. Threshold of 4 keeps
              the UI clean for tiny teams. */}
          {allMembers.length >= 4 && (
            <div className="relative">
              <Search className="absolute start-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <input
                type="search"
                value={memberQuery}
                onChange={e => setMemberQuery(e.target.value)}
                placeholder={
                  locale === "ar"
                    ? "ابحث عن عضو…"
                    : "Search members…"
                }
                className="w-full ps-8 pe-8 py-1.5 text-sm bg-background border border-border rounded-md outline-none focus:border-coral"
              />
              {memberQuery && (
                <button
                  type="button"
                  onClick={() => setMemberQuery("")}
                  className="absolute end-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={locale === "ar" ? "مسح" : "Clear"}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}

          {allMembers.length === 0 && !membersQ.isLoading && (
            <div className="text-sm text-muted-foreground italic">
              {locale === "ar"
                ? "لا أعضاء بعد. ادعُ أحدهم أولاً."
                : "No members yet. Invite someone first."}
            </div>
          )}
          {allMembers.length > 0 && members.length === 0 && (
            <div className="text-sm text-muted-foreground italic">
              {locale === "ar"
                ? `لا يوجد عضو بالاسم "${memberQuery}"`
                : `No member matches "${memberQuery}"`}
            </div>
          )}

          {members.map(m => {
            const my = allPriorities.filter(p => p.userId === m.user.id);
            const draft = getDraft(m.user.id);
            return (
              <section key={m.user.id} className="rounded-xl border border-border bg-background/40 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="font-display text-base">{m.user.name}</div>
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    {m.role}
                  </span>
                  <span className="text-[10px] text-muted-foreground ms-auto">
                    {my.length === 0
                      ? null
                      : locale === "ar"
                        ? `${my.length} ${my.length === 1 ? "أولوية" : "أولويّات"}`
                        : `${my.length} ${my.length === 1 ? "priority" : "priorities"}`}
                  </span>
                </div>

                {my.length > 0 && (
                  <ul className="space-y-1.5 mb-3">
                    {my.map(p => {
                      const tone =
                        PRIORITY_TONE[p.priority] ?? PRIORITY_TONE[2];
                      return (
                        <li
                          key={p.id}
                          className="flex flex-col gap-1.5 text-sm rounded-md bg-background px-2.5 py-1.5 border border-border/60"
                        >
                          <div className="flex items-start gap-2">
                          {editing?.id === p.id ? (
                            // While editing: P1/P2/P3 selector
                            // matches the badge footprint so the row
                            // doesn't reflow.
                            <select
                              value={editing.priority}
                              onChange={e =>
                                setEditing({
                                  ...editing,
                                  priority: parseInt(e.target.value, 10),
                                })
                              }
                              className="shrink-0 text-[10px] uppercase rounded-full border px-1 py-0.5 mt-0.5 bg-background outline-none focus:border-coral"
                            >
                              <option value={1}>P1</option>
                              <option value={2}>P2</option>
                              <option value={3}>P3</option>
                            </select>
                          ) : (
                            <span
                              className={`shrink-0 text-[10px] uppercase rounded-full border px-1.5 py-0.5 mt-0.5 ${tone}`}
                            >
                              P{p.priority}
                            </span>
                          )}
                          <div className="flex-1 min-w-0">
                            {editing?.id === p.id ? (
                              // Inline edit — Enter saves, Esc cancels.
                              // Title-only to keep the affordance simple
                              // (the most common reason to edit is a typo).
                              <input
                                type="text"
                                value={editing.title}
                                autoFocus
                                onChange={e =>
                                  setEditing({
                                    ...editing,
                                    title: e.target.value,
                                  })
                                }
                                onKeyDown={e => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    saveEdit();
                                  } else if (e.key === "Escape") {
                                    setEditing(null);
                                  }
                                }}
                                className="w-full bg-background border border-coral rounded px-1.5 py-0.5 text-sm outline-none"
                              />
                            ) : (
                              <div className="leading-snug break-words">
                                {p.title}
                              </div>
                            )}
                            {p.description && (
                              <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug whitespace-pre-wrap break-words">
                                {p.description}
                              </div>
                            )}
                            {/* Timestamps: always show "created";
                                show "edited" only when updatedAt is
                                meaningfully later (>1 minute) than
                                createdAt, so status flips alone don't
                                pollute the row. */}
                            {(() => {
                              const created = p.createdAt
                                ? new Date(p.createdAt as any)
                                : null;
                              const updated = p.updatedAt
                                ? new Date(p.updatedAt as any)
                                : null;
                              const wasEdited =
                                !!created &&
                                !!updated &&
                                updated.getTime() - created.getTime() > 60_000;
                              const fmt = (d: Date) =>
                                d.toLocaleString(
                                  locale === "ar" ? "ar" : "en",
                                  { dateStyle: "short", timeStyle: "short" },
                                );
                              return created ? (
                                <div className="text-[10px] text-muted-foreground/70 mt-0.5">
                                  {locale === "ar" ? "أُنشئ" : "created"}{" "}
                                  {fmt(created)}
                                  {wasEdited && updated && (
                                    <>
                                      {" · "}
                                      {locale === "ar" ? "حُرّر" : "edited"}{" "}
                                      {fmt(updated)}
                                    </>
                                  )}
                                </div>
                              ) : null;
                            })()}
                            {/* Carry-over chip — rendered when this
                                row was cloned from a prior week's
                                unfinished priority. carryCount >= 2
                                means it's been rolling for multiple
                                weeks → flag in amber as a "chronic
                                straggler" the manager should address. */}
                            {(p as any).carriedFromPriorityId && (
                              <div
                                className={`text-[10px] inline-flex items-center gap-1 mt-0.5 ${
                                  ((p as any).carryCount ?? 1) >= 2
                                    ? "text-amber-700 dark:text-amber-300 font-medium"
                                    : "text-muted-foreground/80"
                                }`}
                                title={
                                  locale === "ar"
                                    ? `رُحِّل ${((p as any).carryCount ?? 1)} ${((p as any).carryCount ?? 1) === 1 ? "مرة" : "مرات"} من أسبوع سابق`
                                    : `Carried over ${((p as any).carryCount ?? 1)} ${((p as any).carryCount ?? 1) === 1 ? "time" : "times"} from a prior week`
                                }
                              >
                                <RotateCcw className="h-2.5 w-2.5" />
                                {locale === "ar"
                                  ? `مُرحَّل ×${(p as any).carryCount ?? 1}`
                                  : `carried ×${(p as any).carryCount ?? 1}`}
                              </div>
                            )}
                          </div>
                          {editing?.id === p.id ? (
                            <>
                              <button
                                type="button"
                                onClick={saveEdit}
                                disabled={updateMut.isPending}
                                aria-label={locale === "ar" ? "حفظ" : "Save"}
                                className="shrink-0 p-1 rounded text-emerald-600 hover:bg-emerald-500/10 transition-colors disabled:opacity-50"
                              >
                                {updateMut.isPending ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Check className="h-3.5 w-3.5" />
                                )}
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditing(null)}
                                aria-label={locale === "ar" ? "إلغاء" : "Cancel"}
                                className="shrink-0 p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() =>
                                  setEditing({
                                    id: p.id,
                                    title: p.title,
                                    priority: p.priority,
                                  })
                                }
                                aria-label={locale === "ar" ? "تعديل" : "Edit"}
                                className="shrink-0 p-1 rounded text-muted-foreground hover:text-coral hover:bg-coral/10 transition-colors"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => removePriority(p.id)}
                                aria-label={locale === "ar" ? "حذف" : "Delete"}
                                className="shrink-0 p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {/* Add row */}
                <div className="rounded-md border border-dashed border-coral/40 bg-coral-soft/15 p-2 space-y-1.5">
                  <input
                    type="text"
                    value={draft.title}
                    onChange={e =>
                      setDraft(m.user.id, { title: e.target.value })
                    }
                    onKeyDown={e => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        addPriority(m.user.id);
                      }
                    }}
                    placeholder={
                      locale === "ar"
                        ? "أضف أولوية… (Enter)"
                        : "Add a priority… (Enter to save)"
                    }
                    className="w-full bg-background border border-border rounded px-2 py-1 text-sm outline-none focus:border-coral"
                  />
                  <div className="flex items-center gap-1.5">
                    {[1, 2, 3].map(lvl => (
                      <button
                        key={lvl}
                        type="button"
                        onClick={() => setDraft(m.user.id, { priority: lvl })}
                        className={`text-[10px] uppercase rounded-full border px-2 py-0.5 transition ${
                          draft.priority === lvl
                            ? PRIORITY_TONE[lvl]
                            : "border-border text-muted-foreground hover:border-coral/50"
                        }`}
                      >
                        P{lvl}
                      </button>
                    ))}
                    <Button
                      onClick={() => addPriority(m.user.id)}
                      disabled={createMut.isPending || !draft.title.trim()}
                      size="sm"
                      className="ms-auto h-7 gap-1 bg-coral hover:bg-coral/90 text-white text-xs"
                    >
                      <Plus className="h-3 w-3" />
                      {locale === "ar" ? "إضافة" : "Add"}
                    </Button>
                  </div>
                </div>
              </section>
            );
          })}

          <div className="text-[11px] text-muted-foreground italic flex items-start gap-1.5">
            <AlertCircle className="h-3 w-3 shrink-0 mt-0.5" />
            <span>
              {locale === "ar"
                ? "هذه قائمة توجيه، لا مراقبة: ترى ما هو مُسنَد لكل عضو، لا تقدّمه المباشر. كل عضو يحدّث حالة أولويّاته بنفسه، ويظهر التقدّم لك عندما يشاركه في ملخّص معتمد."
                : "This is alignment, not tracking: you see what's assigned to each member, not their live progress. Each member updates their own priorities. Progress appears when they share it in an approved receipt."}
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
